import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { BookOpen, Clock, Target, Award } from 'lucide-react';

const AssessmentModal = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [reading, setReading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const testText = `Speed reading is a collection of techniques which are aimed at increasing reading speed without an unacceptable reduction in comprehension or retention. Methods include skimming, meta guiding, and eliminating subvocalization. The many available speed reading training programs include books, videos, software, and seminars.`;

  const questions = [
    {
      q: "What is the main purpose of speed reading?",
      options: ["To read faster with good comprehension", "To skip words", "To memorize everything", "To read backwards"],
      correct: 0
    },
    {
      q: "Which is NOT mentioned as a speed reading method?",
      options: ["Skimming", "Meta guiding", "Highlighting", "Eliminating subvocalization"],
      correct: 2
    }
  ];

  const handleStartReading = () => {
    setReading(true);
    setStartTime(Date.now());
  };

  const handleFinishReading = () => {
    const endTime = Date.now();
    const timeSeconds = (endTime - startTime) / 1000;
    const wordCount = testText.split(' ').length;
    const wpm = Math.round((wordCount / timeSeconds) * 60);
    
    setResults({ wpm, timeSeconds });
    setReading(false);
    setStep(2);
  };

  const handleAnswer = (questionIndex, answerIndex) => {
    setAnswers({ ...answers, [questionIndex]: answerIndex });
  };

  const handleSubmit = async () => {
    const correct = questions.filter((q, i) => answers[i] === q.correct).length;
    const comprehensionScore = Math.round((correct / questions.length) * 100);
    
    // Save to backend
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${BACKEND_URL}/api/assessment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reading_speed: results.wpm,
          comprehension_score: comprehensionScore,
          test_type: 'initial'
        })
      });
    } catch (error) {
      console.error('Failed to save assessment:', error);
    }

    onComplete({ wpm: results.wpm, comprehension: comprehensionScore });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Target className="w-6 h-6 text-indigo-400" />
            Reading Assessment
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Let's establish your baseline reading speed and comprehension
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6 py-4">
            <Card className="bg-slate-900/50 border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-8 h-8 text-indigo-400" />
                <div>
                  <h3 className="font-semibold text-white">Step 1: Reading Test</h3>
                  <p className="text-sm text-gray-400">Read the passage below carefully</p>
                </div>
              </div>
              
              {!reading ? (
                <Button onClick={handleStartReading} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  <Clock className="w-4 h-4 mr-2" />
                  Start Reading Test
                </Button>
              ) : (
                <div>
                  <div className="bg-slate-800 rounded-lg p-6 mb-4">
                    <p className="text-gray-300 leading-relaxed">{testText}</p>
                  </div>
                  <Button onClick={handleFinishReading} className="w-full bg-green-600 hover:bg-green-700">
                    I've Finished Reading
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 py-4">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-4 text-center">
              <p className="text-sm text-white/80 mb-1">Your Reading Speed</p>
              <p className="text-3xl font-bold text-white">{results.wpm} WPM</p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Comprehension Questions</h3>
              {questions.map((q, qIndex) => (
                <Card key={qIndex} className="bg-slate-900/50 border-slate-700 p-4 mb-4">
                  <p className="text-white mb-3">{q.q}</p>
                  <div className="space-y-2">
                    {q.options.map((option, oIndex) => (
                      <button
                        key={oIndex}
                        onClick={() => handleAnswer(qIndex, oIndex)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          answers[qIndex] === oIndex
                            ? 'bg-indigo-600 border-indigo-500 text-white'
                            : 'bg-slate-800 border-slate-700 text-gray-300 hover:border-indigo-500'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              <Award className="w-4 h-4 mr-2" />
              Complete Assessment
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssessmentModal;